/*
 OK FBI Investigation Game
 Designed & Developed by AMW
 CREATED BY // AMW
*/
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SessionProvider } from "@/contexts/SessionContext";
import Dashboard from "@/pages/Dashboard";
import CaseDetail from "@/pages/CaseDetail";
import OverlayPage from "@/pages/OverlayPage";
import { CaseBuilderList, CaseBuilderEditor } from "@/pages/CaseBuilder";
import { CasesList, Leaderboard, StubPage, SettingsPage, Credits } from "@/pages/OtherPages";
import { PlansPage, LeaderboardPage, PublicProfilePage } from "@/pages/Phase2Pages";
import { Toaster } from "sonner";
import { InvestigatorLens } from "@/components/InvestigatorLens";
// CommentsDrawer removed from global mount — belongs in Streamer live tools only.
import { TikTokEventBridge } from "@/components/TikTokEventBridge";
import OpsRoomPage from "@/pages/OpsRoomPage";
import { LoginPage, RequireAuth, RequirePerm, AuthProvider } from "@/pages/AuthPage";
import OwnerSettingsPage from "@/pages/OwnerSettings";
import { TermsPage, PrivacyPage } from "@/pages/LegalPages";
import PublicHomePage from "@/pages/PublicHomePage";
import {
  PublicAboutPage,
  PublicHowToPlayPage,
  PublicCasePreviewPage,
  PublicRanksPage,
  PublicPremiumIdsPage,
  PublicLeaderboardPage,
  PublicPlansPage,
  PublicFaqPage,
} from "@/pages/PublicSectionPages";
// ── P10 perf: OWNER-only pages are lazy-loaded. FREE / VIP / STREAMER users
// never download this ~big JS chunk. React.Suspense below shows a tiny
// placeholder while the chunk is fetched on the first Owner-page visit.
const OwnerNotesCenter = React.lazy(() => import("@/pages/OwnerNotesCenter"));
const OwnerHomepageEditor = React.lazy(() => import("@/pages/OwnerHomepageEditor"));
const VoiceLabPage = React.lazy(() => import("@/pages/VoiceLabPage"));
const StreamerCenterPage = React.lazy(() => import("@/pages/StreamerCenterPage"));
const StreamerToolsPage = React.lazy(() => import("@/pages/StreamerToolsPage"));
const PlatformsIndexPage = React.lazy(() => import("@/pages/platforms/PlatformsIndexPage"));
const PlatformTikTokPage = React.lazy(() => import("@/pages/platforms/PlatformTikTokPage"));
const PlatformTwitchPage = React.lazy(() => import("@/pages/platforms/PlatformTwitchPage"));
const PlatformKickPage = React.lazy(() => import("@/pages/platforms/PlatformKickPage"));
const DesignControlCenterPage = React.lazy(() => import("@/pages/DesignControlCenterPage"));
const WebsiteEditorPage = React.lazy(() => import("@/pages/WebsiteEditorPage"));
const VisualEditorPage = React.lazy(() => import("@/pages/VisualEditorPage"));
const CardTemplatesPage = React.lazy(() => import("@/pages/CardTemplatesPage"));
const BadgesPage = React.lazy(() => import("@/pages/BadgesPage"));
const DesignHub = React.lazy(() => import("@/pages/hubs/DesignHub"));
const InvestigatorsHub = React.lazy(() => import("@/pages/hubs/InvestigatorsHub"));
const RewardsHub = React.lazy(() => import("@/pages/hubs/RewardsHub"));
const CasesHub = React.lazy(() => import("@/pages/hubs/CasesHub"));
const BroadcastHub = React.lazy(() => import("@/pages/hubs/BroadcastHub"));
const MediaHub = React.lazy(() => import("@/pages/hubs/MediaHub"));
const SettingsHub = React.lazy(() => import("@/pages/hubs/SettingsHub"));
const PackagesConfigPage = React.lazy(() => import("@/pages/PackagesConfigPage"));
const OwnerProgressionPage = React.lazy(() => import("@/pages/OwnerProgressionPage"));
const NavBuilderPage = React.lazy(() => import("@/pages/NavBuilderPage"));
const OwnerCenterPage = React.lazy(() => import("@/pages/OwnerCenterPage"));
const EvidenceNotificationsPage = React.lazy(() => import("@/pages/EvidenceNotificationsPage"));
import BroadcastCardWallPage from "@/pages/BroadcastCardWallPage";
import InvestigatorProfilePage from "@/pages/InvestigatorProfilePage";
import AnnouncementsPage, { SiteTicker } from "@/pages/AnnouncementsPage";
import CustomPageView from "@/pages/CustomPageView";
import { SquadLanding, SquadJoin, SquadRoom, SquadDisplay } from "@/pages/SquadPages";
import HelpAssistant from "@/components/HelpAssistant";
import InvestigatorInboxDrawer from "@/components/InvestigatorInboxDrawer";
import AdminUnlockedRoute from "@/components/AdminUnlockedRoute";
import { useAuth } from "@/pages/AuthPage";
import TikTokWelcomePage from "@/pages/TikTokWelcomePage";
import { BASE_URL } from "@/lib/api";

function TikTokCallbackBridge() {
  useEffect(() => {
    const apiBase = (BASE_URL || "").replace(/\/+$/, "");
    window.location.replace(`${apiBase}/api/auth/tiktok/callback${window.location.search}`);
  }, []);

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-black flex items-center justify-center text-gold text-[11px] tracking-[0.35em] font-mono-ar animate-pulse"
    >
      جاري إتمام التسجيل…
    </div>
  );
}

function GlobalTickerMount() {
  // Skip mounting the site ticker on OBS-safe / broadcast surfaces to keep
  // those clean; the broadcast page renders its own scope="broadcast" ticker.
  const loc = useLocation();
  const p = loc.pathname;
  if (p.startsWith("/overlay") || p.startsWith("/broadcast/")) return null;
  return <SiteTicker scope="site" />;
}

function OwnerOnlyRoute({ children }) {
  const { isOwner, loaded } = useAuth();
  if (!loaded) {
    return <div dir="rtl" className="min-h-screen bg-black flex items-center justify-center text-gold text-[10px] tracking-[0.35em] font-mono-ar">جاري التحقق من الصلاحيات...</div>;
  }
  if (!isOwner) return <Navigate to="/app" replace />;
  return children;
}

function StreamerOrOwnerRoute({ children }) {
  const { user, isOwner, loaded } = useAuth();
  if (!loaded) {
    return <div dir="rtl" className="min-h-screen bg-black flex items-center justify-center text-gold text-[10px] tracking-[0.35em] font-mono-ar">جاري التحقق من الصلاحيات...</div>;
  }
  const plan = (user?.membership_plan || "").toUpperCase();
  const role = (user?.role || "").toUpperCase();
  if (isOwner || plan === "STREAMER" || role === "STREAMER") return children;
  return <Navigate to="/plans" replace />;
}

function AuthedGlobalMounts() {
  // P10 perf: helper-drawer / assistant / TikTok bridge / inbox are only
  // useful for authenticated users. Mounting them for PUBLIC visitors wastes
  // network + memory (each starts its own polling loop).  Auth guard keeps
  // homepage and legal pages fully static and lightweight.
  const { user, isOwner } = useAuth();

  // Global safety net: swallow axios 401/403 promise rejections so a locked
  // resource never triggers the React runtime-error overlay.  Component-level
  // error handling still runs first — this ONLY catches truly unhandled cases.
  useEffect(() => {
    const onRejection = (e) => {
      const err = e?.reason;
      const status = err?.response?.status || err?.status;
      if (status === 401 || status === 403) {
        e.preventDefault();          // suppress React overlay
        // eslint-disable-next-line no-console
        console.debug("[safety] swallowed axios", status, err?.config?.url);
      }
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
  }, []);

  if (!user) return null;
  const plan = (user.membership_plan || "").toUpperCase();
  const isStreamerOrOwner = isOwner || plan === "STREAMER";
  return (
    <>
      {/* TikTok Event bridge attempts a ws://localhost connection and posts
          bridge-state on every reconnect — only useful for the streaming PC.
          Gate it to STREAMER + OWNER so FREE/VIP/LEGENDARY/ROYAL don't emit
          8+ pointless bridge-state POSTs per page load. */}
      {isStreamerOrOwner && <TikTokEventBridge />}
      <HelpAssistant />
      <InvestigatorInboxDrawer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <SessionProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" />
          <GlobalTickerMount />
          <InvestigatorLens />
          <AuthedGlobalMounts />
          <React.Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#050505]">
              <div className="text-gold text-[10px] tracking-[0.4em] font-mono-ar animate-pulse">LOADING…</div>
            </div>
          }>
          <Routes>
            {/* Public / broadcast-safe surfaces */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/tiktok/callback" element={<TikTokCallbackBridge />} />
            <Route path="/overlay" element={<OverlayPage />} />
            <Route path="/broadcast/card-wall" element={<BroadcastCardWallPage />} />
            {/* Public legal — no auth required (TikTok Review will visit these) */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* Public Cinematic Homepage — the new /  ; internal Dashboard moved to /app */}
            <Route path="/" element={<PublicHomePage />} />
            {/* Public isolated section pages (real routes, not anchor scrolls) */}
            <Route path="/about" element={<PublicAboutPage />} />
            <Route path="/how-to-play" element={<PublicHowToPlayPage />} />
            <Route path="/case-preview" element={<PublicCasePreviewPage />} />
            <Route path="/ranks" element={<PublicRanksPage />} />
            <Route path="/premium-ids" element={<PublicPremiumIdsPage />} />
            <Route path="/leaderboard-public" element={<PublicLeaderboardPage />} />
            <Route path="/plans-public" element={<PublicPlansPage />} />
            <Route path="/faq" element={<PublicFaqPage />} />
            {/* Protected host routes */}
            <Route path="/host/case-builder" element={<RequireAuth><RequirePerm perm="CREATE_CASE"><CaseBuilderList /></RequirePerm></RequireAuth>} />
            <Route path="/host/case-builder/:id" element={<RequireAuth><RequirePerm perm="EDIT_CASE"><CaseBuilderEditor /></RequirePerm></RequireAuth>} />
            <Route path="/ops" element={<RequireAuth><StreamerOrOwnerRoute><OpsRoomPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/welcome/tiktok" element={<RequireAuth><TikTokWelcomePage /></RequireAuth>} />
            <Route path="/app" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/cases" element={<RequireAuth><CasesList /></RequireAuth>} />
            <Route path="/cases/:caseId" element={<RequireAuth><CaseDetail /></RequireAuth>} />
            <Route path="/cases/:caseId/*" element={<RequireAuth><CaseDetail /></RequireAuth>} />
            <Route path="/case/:caseId" element={<RequireAuth><CaseDetail /></RequireAuth>} />
            <Route path="/suspects" element={<RequireAuth><StubPage title="المشتبه بهم" /></RequireAuth>} />
            <Route path="/evidence" element={<RequireAuth><StubPage title="الأدلة" /></RequireAuth>} />
            <Route path="/forensics" element={<RequireAuth><StubPage title="التحليل الجنائي" /></RequireAuth>} />
            <Route path="/timeline" element={<RequireAuth><StubPage title="الخط الزمني" /></RequireAuth>} />
            <Route path="/notes" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><OwnerNotesCenter /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/homepage" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><OwnerHomepageEditor /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/voice-lab" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><VoiceLabPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host" element={<RequireAuth><OwnerOnlyRoute><OwnerCenterPage /></OwnerOnlyRoute></RequireAuth>} />
            {/* Seven category hubs — thin navigational wrappers, existing routes preserved */}
            <Route path="/host/design-center"     element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><DesignHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/investigators"     element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><InvestigatorsHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/rewards"           element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><RewardsHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/cases"             element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><CasesHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/evidence-notifications" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><EvidenceNotificationsPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/broadcast"         element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><BroadcastHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/media"             element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><MediaHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/settings-security" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><SettingsHub /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/design" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><DesignControlCenterPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/website-editor" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><WebsiteEditorPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/visual-editor"  element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><VisualEditorPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/card-templates" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><CardTemplatesPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/badges" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><BadgesPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/announcements" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><AnnouncementsPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/packages-config" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><PackagesConfigPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/progression" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><OwnerProgressionPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/nav-builder" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><NavBuilderPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/p/:pageId" element={<RequireAuth><CustomPageView /></RequireAuth>} />
            {/* ── فرقة التحقيق (Squad Mode) ────────────────────────
                / squad / join / display are LOCAL-multiplayer surfaces.
                Landing + Room require auth (leader/participant).
                Join is public so guests can enter a 6-digit code.
                Display is public — display token comes in the URL. */}
            <Route path="/squad" element={<RequireAuth><SquadLanding /></RequireAuth>} />
            <Route path="/squad/join" element={<SquadJoin />} />
            <Route path="/squad/session/:sid" element={<SquadRoom />} />
            <Route path="/squad/display/:sid" element={<SquadDisplay />} />
            <Route path="/investigator/:userId" element={<InvestigatorProfilePage />} />
            <Route path="/profile" element={<RequireAuth><InvestigatorProfilePage /></RequireAuth>} />
            <Route path="/host/team" element={<RequireAuth><OwnerOnlyRoute><AdminUnlockedRoute><OwnerSettingsPage /></AdminUnlockedRoute></OwnerOnlyRoute></RequireAuth>} />
            <Route path="/host/streamer" element={<RequireAuth><StreamerOrOwnerRoute><StreamerCenterPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/host/streamer-tools" element={<Navigate to="/host/platforms" replace />} />
            <Route path="/host/platforms" element={<RequireAuth><StreamerOrOwnerRoute><PlatformsIndexPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/host/platforms/tiktok" element={<RequireAuth><StreamerOrOwnerRoute><PlatformTikTokPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/host/platforms/twitch" element={<RequireAuth><StreamerOrOwnerRoute><PlatformTwitchPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/host/platforms/kick" element={<RequireAuth><StreamerOrOwnerRoute><PlatformKickPage /></StreamerOrOwnerRoute></RequireAuth>} />
            <Route path="/leaderboard" element={<RequireAuth><LeaderboardPage /></RequireAuth>} />
            <Route path="/plans" element={<RequireAuth><PlansPage /></RequireAuth>} />
            <Route path="/profile/:agentId" element={<RequireAuth><PublicProfilePage /></RequireAuth>} />
            <Route path="/credits" element={<RequireAuth><Credits /></RequireAuth>} />
            <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </React.Suspense>
        </BrowserRouter>
      </SessionProvider>
    </AuthProvider>
  );
}
export default App;
